import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { FormLabel } from './shared/FormLabel';
import { AiWriterModal } from './AiWriterModal';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import clsx from 'clsx';
import plusIcon from '@/assets/images/svg/plus_icon.svg';
import penIcon from '@/assets/images/svg/pen_icon.svg';
import trashIcon from '@/assets/images/svg/trash_icon.svg';
import { ConsentCheckbox } from '../ConsentCheckbox';

const MAX_GREETINGS = 3;
const MAX_GREETING_LENGTH = 300;

const basicInfoSchema = z.object({
  name: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(20, 'Name cannot exceed 20 characters'),
  domain: z.string().min(1, 'Domain is required'),
  category: z.string().min(1, 'Category is required'),
  bio: z.string().min(10, 'Bio must be at least 10 characters'),
});

type BasicInfoValues = z.infer<typeof basicInfoSchema>;

interface BasicInfoSectionProps {
  onCreate: (data: BasicInfoValues) => Promise<void>;
  onSaveAndExit: (data: Partial<BasicInfoValues>) => Promise<void>;
  isSubmitting: boolean;
  initialData?: BasicInfoValues;
  draftId?: string | null;
  greetings: string[];
  onGreetingsChange: (greetings: string[]) => void;
  isPublicFigure: boolean;
  onPublicFigureChange: (value: boolean) => void;
}

/** Step 1 form: name, domain, category, greetings, bio, and public figure toggle. */
export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  onCreate,
  onSaveAndExit,
  isSubmitting,
  initialData,
  draftId: _draftId,
  greetings,
  onGreetingsChange,
  isPublicFigure,
  onPublicFigureChange,
}) => {
  const { domains, categories } = useTaxonomy();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<BasicInfoValues>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: initialData || {
      name: '',
      domain: '',
      category: '',
      bio: '',
    },
  });

  React.useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const handleSaveClick = async () => {
    const values = getValues();
    await onSaveAndExit(values);
  };

  const [identityConsent, setIdentityConsent] = React.useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = React.useState(false);

  const handlePickSuggestion = (text: string) => {
    setValue('bio', text, { shouldValidate: true });
  };

  const nameValue = watch('name');

  const updateGreeting = (index: number, value: string) => {
    const updated = greetings.map((greeting, idx) => (idx === index ? value : greeting));
    onGreetingsChange(updated);
  };

  const addGreeting = () => {
    if (greetings.length < MAX_GREETINGS) {
      onGreetingsChange([...greetings, '']);
    }
  };

  const removeGreeting = (index: number) => {
    if (greetings.length > 1) {
      onGreetingsChange(greetings.filter((_, i) => i !== index));
    }
  };

  const inputClasses = clsx(
    'h-[47px] w-full rounded-10 px-4',
    'border border-gray-200 bg-white',
    'font-sans text-16 font-normal leading-160 tracking-neg-2 text-brand-800',
    'placeholder:text-gray-370',
    'focus:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-300'
  );

  return (
    <div className={clsx('animate-slide-in w-full', 'mt-12', 'wide:mx-auto wide:max-w-[1000px]')}>
      <form className='flex flex-col gap-8'>
        {/* Name */}
        <div className='flex flex-col gap-3'>
          <FormLabel
            label='Give Your Echo a Name'
            subtitle='What should fans call you? This could be your real name, stage name, or a descriptive title.'
          />
          <div className='relative'>
            <input
              className={clsx(inputClasses, 'pr-16')}
              placeholder='e.g., Coach Marcus, DJ Sarah, Chef Antonio'
              maxLength={20}
              {...register('name')}
            />
            <span className='pointer-events-none absolute inset-y-0 right-4 flex items-center font-sans text-14 text-gray-370'>
              {nameValue?.length ?? 0}/20
            </span>
          </div>
          <p className='font-sans text-12 font-medium leading-27 text-red-450'>
            *do not use the name, image, likeness, or identity of another person without
            authorization.
          </p>
          {errors.name && <p className='text-xs text-red-500'>{errors.name.message}</p>}
        </div>

        {/* Public Figure Toggle */}
        <label className='flex cursor-pointer items-center gap-3'>
          <div
            className={clsx(
              'relative h-[24px] w-[44px] shrink-0 rounded-full transition-colors',
              isPublicFigure ? 'bg-brand-600' : 'bg-gray-300'
            )}
            onClick={() => onPublicFigureChange(!isPublicFigure)}
          >
            <div
              className={clsx(
                'absolute top-[2px] size-[20px] rounded-full bg-white shadow transition-transform',
                isPublicFigure ? 'translate-x-[22px]' : 'translate-x-[2px]'
              )}
            />
          </div>
          <span className='font-sans text-14 font-medium text-brand-800'>
            This is a public figure / celebrity
          </span>
        </label>

        {/* Domain & Category Row */}
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <div className='flex flex-col gap-3'>
            <FormLabel label='Choose Your Domain' subtitle='What field are you in?' />
            <div className='relative'>
              <select className={clsx(inputClasses, 'appearance-none')} {...register('domain')}>
                <option value=''>Select Domain...</option>
                {domains.map((domain) => (
                  <option key={domain.id} value={domain.name}>
                    {domain.name}
                  </option>
                ))}
              </select>
              <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500'>
                <svg className='size-4 fill-current' viewBox='0 0 20 20'>
                  <path d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' />
                </svg>
              </div>
            </div>
            {errors.domain && <p className='text-xs text-red-500'>{errors.domain.message}</p>}
          </div>

          <div className='flex flex-col gap-3'>
            <FormLabel label='Your Category' subtitle="What's your specialty?" />
            <div className='relative'>
              <select className={clsx(inputClasses, 'appearance-none')} {...register('category')}>
                <option value=''>Select Category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500'>
                <svg className='size-4 fill-current' viewBox='0 0 20 20'>
                  <path d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' />
                </svg>
              </div>
            </div>
            {errors.category && <p className='text-xs text-red-500'>{errors.category.message}</p>}
          </div>
        </div>

        {/* Greetings (up to 3) */}
        <div className='flex flex-col gap-3'>
          <FormLabel
            label={`Greeting (up to ${MAX_GREETINGS})`}
            subtitle="Introduction of your echo — doesn't affect the dialogue effect"
          />
          {greetings.map((greeting, index) => (
            <div key={index} className='flex items-center gap-3'>
              <div className='relative flex-1'>
                <textarea
                  className={clsx(inputClasses, 'min-h-[120px] py-3 pb-8')}
                  placeholder='Type the short hello your D.ECHO should open with — friendly, in character, and ending with a simple question to get the user talking.'
                  maxLength={MAX_GREETING_LENGTH}
                  value={greeting}
                  onChange={(e) => updateGreeting(index, e.target.value)}
                />
                <span className='pointer-events-none absolute bottom-2 right-4 font-sans text-14 text-gray-370'>
                  {greeting.length}/{MAX_GREETING_LENGTH}
                </span>
              </div>
              {greetings.length > 1 && (
                <button
                  type='button'
                  onClick={() => removeGreeting(index)}
                  className='flex size-8 shrink-0 items-center justify-center rounded-full transition-opacity hover:opacity-60'
                >
                  <img src={trashIcon} alt='Remove greeting' className='size-[18px]' />
                </button>
              )}
            </div>
          ))}
          {greetings.length < MAX_GREETINGS && (
            <div className='flex justify-end'>
              <Button
                type='button'
                variant='white-pill'
                className='h-[32px] gap-1.5'
                onClick={addGreeting}
              >
                <img src={plusIcon} alt='' className='size-4' />
                Add new greeting
              </Button>
            </div>
          )}
        </div>

        {/* Bio */}
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <label className='font-sans text-16 font-medium leading-21 text-dark-100'>
              Your Story
            </label>
            <div className='flex items-center justify-between'>
              <p className='font-sans text-14 font-medium leading-27 text-gray-530'>
                Write 2-3 sentences about who you are and what you&apos;re known for. This is the
                first thing fans will see.
              </p>
              <Button
                type='button'
                variant='black-pill'
                className='h-[32px] shrink-0 gap-1.5'
                onClick={() => setIsAiModalOpen(true)}
              >
                <img src={penIcon} alt='' className='size-4' />
                AI writer
              </Button>
            </div>
          </div>
          <textarea
            className={clsx(inputClasses, 'min-h-[120px] py-3')}
            placeholder="I'm a 3x NBA champion Who built my career on discipline and mental toughness. After retiring from professional basketball, I dedicated myself to coaching the next generation of athletes. Now, I help aspiring players develop not just their skills, but their mindset for success."
            {...register('bio')}
          />
          {errors.bio && <p className='text-xs text-red-500'>{errors.bio.message}</p>}
        </div>

        {/* Consent */}
        <div className='flex flex-col gap-3'>
          <h3 className='font-sans text-16 font-extrabold leading-21 text-dark-100'>Consent</h3>
          <p className='font-sans text-14 font-medium leading-[22px] text-dark-450'>
            I confirm that I am this person, or that I have all necessary rights and permissions to
            use their name, image, likeness, identity, and related content to create and monetize
            this AI avatar.
          </p>
          <ConsentCheckbox
            checked={identityConsent}
            onChange={setIdentityConsent}
            label='I confirm I have the right to use this identity'
            className='inline-flex w-auto self-start border-gray-310 px-4 py-3'
            labelClassName='text-gray-600'
          />
        </div>

        {/* Footer Actions */}
        <div className='mt-8 flex flex-col items-center justify-between gap-6 md:flex-row'>
          <div className='w-full md:w-auto'>
            <Button
              type='button'
              onClick={handleSubmit(onCreate)}
              variant='purple-dark'
              disabled={isSubmitting || !identityConsent}
              className='h-[48px] w-full rounded-30 text-16 font-bold uppercase tracking-wide md:w-glow'
            >
              {isSubmitting ? 'Creating...' : 'Continue'}
            </Button>
          </div>

          <button
            type='button'
            onClick={handleSaveClick}
            disabled={isSubmitting}
            className='font-sans text-16 font-semibold text-gray-650 underline decoration-solid transition-colors hover:text-brand-800 disabled:opacity-50'
          >
            Save for Later
          </button>
        </div>
      </form>

      {/* AI Writer Modal */}
      <AiWriterModal
        isOpen={isAiModalOpen}
        onClose={() => setIsAiModalOpen(false)}
        onPickSuggestion={handlePickSuggestion}
        formValues={{
          name: watch('name') || '',
          domain: watch('domain') || '',
          category: watch('category') || '',
        }}
        isPublicFigure={isPublicFigure}
      />
    </div>
  );
};
